
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card';
import { ArrowLeft, Save, AlertTriangle } from 'lucide-react';
import { useConfirm } from '../../../hooks/use-confirm';
import { InvoiceSourceOption, legacyApi } from '../../../services/legacyApi';
import type { LegacyApiError } from '../../../services/legacyApi';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { toast } from 'sonner';
import EntityTypeaheadPicker, { PickerOption } from '../../../components/search/EntityTypeaheadPicker';
import { FlowCoachPanel } from '../../../components/workflow/FlowCoachPanel';
import { WorkflowRightPanel } from '../../../components/workflow/WorkflowRightPanel';
import { WorkflowRightSidebar } from '../../../components/workflow/WorkflowRightSidebar';

interface InvoiceItemState {
    estimate_part_id: number;
    part_name: string;
    ordered_qty: number;
    delivered_qty: number; // Added
    invoiced_qty: number;
    remaining_invoice_qty: number; // Delivered balance (Delivered - Invoiced)
    current_invoice_qty: string | number;
    unit_price: number;
}

interface ActiveTaxInvoiceConflict {
    invoice_id: number;
    status_id: number | null;
    status_name?: string | null;
}

const toLineKey = (value: unknown): number | null => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed === 0) return null;
    return parsed;
};

const formatQty = (value: number): string => {
    if (!Number.isFinite(value)) return '0';
    if (Math.abs(value - Math.round(value)) < 1e-6) return String(Math.round(value));
    return value.toFixed(2);
};

export const InvoiceForm: React.FC = () => {
    // ... (existing hooks)
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const { soId, invoiceId } = useParams<{ soId: string; invoiceId: string }>();
    const [searchParams] = useSearchParams();
    const estimateIdParam = searchParams.get('estimate_id');
    const woIdParam = searchParams.get('wo_id');
    const invoiceTypeParam = (searchParams.get('invoice_type') || '').toUpperCase();

    const [selectedEstimateId, setSelectedEstimateId] = useState<number | ''>('');
    const [selectedSourceKey, setSelectedSourceKey] = useState<string>('');
    const [selectedSoId, setSelectedSoId] = useState<number | null>(null);
    const [selectedWoId, setSelectedWoId] = useState<number | null>(null);
    const [invoiceType, setInvoiceType] = useState<'TAX' | 'PROFORMA'>(
        invoiceTypeParam === 'PROFORMA' ? 'PROFORMA' : 'TAX'
    );
    const [sourceQuery, setSourceQuery] = useState('');
    const [sourceOptions, setSourceOptions] = useState<InvoiceSourceOption[]>([]);
    const [sourceLoading, setSourceLoading] = useState(false);
    const [customerFilterId, setCustomerFilterId] = useState<number | null>(null);
    const [soStatusFilter, setSoStatusFilter] = useState<number | null>(null);
    const [woStatusFilter, setWoStatusFilter] = useState<number | null>(null);
    const [customers, setCustomers] = useState<{ customer_id: number; customer_name: string }[]>([]);
    const [customerPoNo, setCustomerPoNo] = useState('');
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [invoiceNotes, setInvoiceNotes] = useState('');

    // Items State
    const [items, setItems] = useState<InvoiceItemState[]>([]);
    const [lineErrors, setLineErrors] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [syncingDeliveries, setSyncingDeliveries] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [invoiceStatusId, setInvoiceStatusId] = useState<number | null>(null);
    const [activeTaxInvoiceConflict, setActiveTaxInvoiceConflict] = useState<ActiveTaxInvoiceConflict | null>(null);

    useEffect(() => {
        if (invoiceId) return;
        let cancelled = false;
        const timer = window.setTimeout(async () => {
            try {
                setSourceLoading(true);
                const rows = await legacyApi.sales.listInvoiceSourceOptions({
                    query: sourceQuery || undefined,
                    customer_id: customerFilterId || undefined,
                    so_status_ids: soStatusFilter ? [soStatusFilter] : undefined,
                    wo_status_ids: woStatusFilter ? [woStatusFilter] : undefined,
                    limit: 80,
                });
                if (!cancelled) setSourceOptions(rows || []);
            } catch (err) {
                if (!cancelled) console.error('Failed to load invoice source options', err);
            } finally {
                if (!cancelled) setSourceLoading(false);
            }
        }, 220);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [invoiceId, sourceQuery, customerFilterId, soStatusFilter, woStatusFilter]);

    useEffect(() => {
        legacyApi.customers.list()
            .then((rows) => setCustomers((rows || []).map((c: any) => ({ customer_id: Number(c.customer_id), customer_name: String(c.customer_name || '') }))))
            .catch(console.error);
    }, []);

    useEffect(() => {
        // Route/mode switch can reuse the same component instance; clear stale conflict UI.
        setError(null);
        setActiveTaxInvoiceConflict(null);
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                if (invoiceId) {
                    // EDIT MODE
                    const inv = await legacyApi.sales.getInvoice(Number(invoiceId));
                    if (inv) {
                        setSelectedEstimateId(inv.estimate_id);
                        setSelectedSoId(inv.so_id || null);
                        setSelectedWoId(inv.wo_id || null);
                        if (inv.so_id) {
                            const key = inv.wo_id ? `SO:${inv.so_id}:WO:${inv.wo_id}` : `SO:${inv.so_id}`;
                            setSelectedSourceKey(key);
                        }
                        setInvoiceType(inv.invoice_type === 'PROFORMA' ? 'PROFORMA' : 'TAX');
                        setInvoiceStatusId(Number(inv.status_id || 0) || null);
                        setCustomerPoNo(inv.customer_po_no || '');
                        setInvoiceDate(inv.invoice_date ? inv.invoice_date.split('T')[0] : new Date().toISOString().split('T')[0]);
                        setInvoiceNotes(inv.invoice_notes || '');

                        if (inv.details) {
                            const soDetailByPart = new Map<number, any>();
                            if (inv.so_id) {
                                try {
                                    const so = await legacyApi.sales.getOrder(Number(inv.so_id));
                                    (so?.details || []).forEach((line: any) => {
                                        const key = toLineKey(line?.estimate_part_id);
                                        if (key !== null) soDetailByPart.set(key, line);
                                    });
                                } catch (e) {
                                    console.error('Failed to load SO details for invoice edit context', e);
                                }
                            }

                            const mappedItems: InvoiceItemState[] = inv.details.map((d: any) => {
                                const estimatePartId = Number(d.estimate_part_id || 0);
                                const currentQty = Number(d.quantity || 0);
                                const soLine = soDetailByPart.get(estimatePartId);
                                const ordered = Number(soLine?.quantity || d.quantity || 0);
                                const delivered = Number(soLine?.delivered_qty || 0);
                                const soInvoiced = Number(soLine?.invoiced_qty || 0);
                                const invoicedExcludingCurrent = Math.max(0, soInvoiced - currentQty);
                                const taxRemainingExcludingCurrent = Number(
                                    soLine?.remaining_invoice_qty !== undefined
                                        ? soLine.remaining_invoice_qty
                                        : Math.max(0, delivered - soInvoiced)
                                );
                                const taxRemainingForEdit = Math.max(0, taxRemainingExcludingCurrent + currentQty);

                                return {
                                    estimate_part_id: estimatePartId,
                                    part_name: d.part_name || `Item ${estimatePartId}`,
                                    ordered_qty: ordered,
                                    delivered_qty: delivered,
                                    invoiced_qty: invoicedExcludingCurrent,
                                    remaining_invoice_qty: taxRemainingForEdit,
                                    current_invoice_qty: currentQty,
                                    unit_price: Number(d.unit_price || 0)
                                };
                            });
                            setItems(mappedItems);
                        }
                    }
                } else if (soId) {
                    // CREATE MODE (from SO)
                    setInvoiceStatusId(null);
                    const so = await legacyApi.sales.getOrder(Number(soId));
                    if (so) {
                        setSelectedEstimateId(so.estimate_id);
                        setSelectedSoId(Number(so.so_id));
                        setSelectedSourceKey(`SO:${Number(so.so_id)}`);
                        if (woIdParam && !Number.isNaN(Number(woIdParam))) {
                            setSelectedWoId(Number(woIdParam));
                        }
                        setCustomerPoNo(so.customer_po_no || '');
                        if (invoiceTypeParam === 'PROFORMA') {
                            setInvoiceType('PROFORMA');
                        } else if (invoiceTypeParam === 'TAX') {
                            setInvoiceType('TAX');
                        } else if ((so.estimate_id || 0) <= 0) {
                            setInvoiceType('PROFORMA');
                        }

                        if (so.details) {
                            const mappedItems: InvoiceItemState[] = so.details.map((d: any) => {
                                const delivered = d.delivered_qty || 0;
                                const invoiced = d.invoiced_qty || 0;
                                const ordered = d.quantity || 0;

                                const suggestion = invoiceTypeParam === 'PROFORMA'
                                    ? Math.max(0, ordered - invoiced)
                                    : Math.max(0, delivered - invoiced);

                                return {
                                    estimate_part_id: d.estimate_part_id,
                                    part_name: d.part_name,
                                    ordered_qty: ordered,
                                    delivered_qty: delivered,
                                    invoiced_qty: invoiced,
                                    remaining_invoice_qty: d.remaining_invoice_qty !== undefined ? d.remaining_invoice_qty : Math.max(0, delivered - invoiced),
                                    current_invoice_qty: suggestion,
                                    unit_price: d.unit_price
                                };
                            });
                            setItems(mappedItems);
                        }
                    }
                } else if (estimateIdParam) {
                    // Legacy compatibility for estimate deep-links: source must still be SO-based.
                    setInvoiceStatusId(null);
                    const parsed = Number(estimateIdParam);
                    if (!Number.isNaN(parsed)) {
                        setSelectedEstimateId(parsed);
                    }
                }
            } catch (err) {
                console.error(err);
                setError("Failed to load data");
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, [soId, woIdParam, invoiceId, estimateIdParam, invoiceTypeParam]);

    // ... (keep helper functions like formatInvoiceId, handleQtyChange, handleSave)
    const formatInvoiceId = (id?: string | number) => {
        if (!id) return '';
        const n = Number(id);
        if (Number.isNaN(n)) return String(id);
        return `HC/INV/${String(n).padStart(4, '0')}`;
    };

    const sourcePickerOptions: PickerOption[] = sourceOptions.map((opt) => ({
        value: opt.source_key,
        label: opt.wo_id ? `${opt.so_number} • ${opt.wo_number}` : opt.so_number,
        description: opt.customer_name || undefined,
        badges: [
            { label: `SO ${opt.so_status_label}`, tone: 'blue' },
            ...(opt.wo_id ? [{ label: `WO ${opt.wo_status_label || 'Draft'}`, tone: 'amber' as const }] : []),
        ],
        colorToken: opt.color_token,
    }));

    const customerPickerOptions: PickerOption[] = useMemo(
        () => [
            { value: '', label: 'All Customers' },
            ...customers.map((c) => ({
                value: String(c.customer_id),
                label: c.customer_name,
            })),
        ],
        [customers]
    );

    const resolveLineMaxQty = (item: InvoiceItemState) => {
        if (invoiceType === 'PROFORMA') {
            return Math.max(0, Number(item.ordered_qty || 0) - Number(item.invoiced_qty || 0));
        }
        return Math.max(0, Number(item.remaining_invoice_qty || 0));
    };

    const handleQtyChange = (index: number, val: string) => {
        const newItems = [...items];
        newItems[index].current_invoice_qty = val;
        setItems(newItems);
        const numericValue = Number(val || 0);
        if (Number.isNaN(numericValue) || numericValue < 0) {
            setLineErrors((prev) => ({ ...prev, [index]: 'Quantity must be 0 or greater.' }));
            return;
        }
        const maxQty = resolveLineMaxQty(newItems[index]);
        if (numericValue > maxQty + 0.001) {
            setLineErrors((prev) => ({
                ...prev,
                [index]: `Max allowed is ${maxQty} (${invoiceType === 'PROFORMA' ? 'ordered-not-invoiced' : 'delivered-not-invoiced'}).`,
            }));
            return;
        }
        setLineErrors((prev) => {
            if (!prev[index]) return prev;
            const next = { ...prev };
            delete next[index];
            return next;
        });
    };

    const handleSelectSource = async (sourceKey: string) => {
        const match = sourceOptions.find((opt) => opt.source_key === sourceKey);
        if (!match) return;
        setSelectedSourceKey(sourceKey);
        setSelectedSoId(match.so_id);
        setSelectedWoId(match.wo_id || null);
        setInvoiceStatusId(null);
        setError(null);
        setActiveTaxInvoiceConflict(null);
        setLineErrors({});
        try {
            setLoading(true);
            const so = await legacyApi.sales.getOrder(match.so_id);
            setSelectedEstimateId(Number(so.estimate_id || 0));
            setCustomerPoNo(so.customer_po_no || '');
            if (!invoiceId && !soId && invoiceTypeParam !== 'PROFORMA' && invoiceTypeParam !== 'TAX') {
                setInvoiceType(Number(so.estimate_id || 0) <= 0 ? 'PROFORMA' : 'TAX');
            }
            const mappedItems: InvoiceItemState[] = (so.details || []).map((d: any) => {
                const delivered = d.delivered_qty || 0;
                const invoiced = d.invoiced_qty || 0;
                const ordered = d.quantity || 0;
                const suggestion = (invoiceTypeParam === 'PROFORMA' || invoiceType === 'PROFORMA')
                    ? Math.max(0, ordered - invoiced)
                    : Math.max(0, delivered - invoiced);
                return {
                    estimate_part_id: d.estimate_part_id,
                    part_name: d.part_name,
                    ordered_qty: ordered,
                    delivered_qty: delivered,
                    invoiced_qty: invoiced,
                    remaining_invoice_qty: d.remaining_invoice_qty !== undefined ? d.remaining_invoice_qty : Math.max(0, delivered - invoiced),
                    current_invoice_qty: suggestion,
                    unit_price: d.unit_price,
                };
            });
            setItems(mappedItems);
        } catch (err: any) {
            console.error(err);
            setError(err?.message || 'Failed to load source sale order');
        } finally {
            setLoading(false);
        }
    };

    const canSyncFromDeliveries = Boolean(
        invoiceId &&
        invoiceType === 'TAX' &&
        Number(invoiceStatusId || 0) === 1 &&
        Number(selectedSoId || 0) > 0
    );

    const handleSyncFromDeliveries = async () => {
        if (!canSyncFromDeliveries) return;
        try {
            setSyncingDeliveries(true);
            setError(null);

            const so = await legacyApi.sales.getOrder(Number(selectedSoId));
            const soLines = so?.details || [];

            const soDetailByPart = new Map<number, any>();
            soLines.forEach((line: any) => {
                const key = toLineKey(line?.estimate_part_id);
                if (key !== null) soDetailByPart.set(key, line);
            });

            if (soDetailByPart.size === 0) {
                toast.info('No SO lines available for TAX sync.');
                return;
            }

            const nextItems = [...items];
            const itemIndexByPart = new Map<number, number>();
            nextItems.forEach((line, idx) => {
                const key = toLineKey(line.estimate_part_id);
                if (key !== null) itemIndexByPart.set(key, idx);
            });

            let changedLines = 0;
            let addedQtyTotal = 0;

            soDetailByPart.forEach((soLine, estimatePartId) => {
                const ordered = Number(soLine?.quantity || 0);
                const delivered = Number(soLine?.delivered_qty || 0);
                const soInvoiced = Number(soLine?.invoiced_qty || 0);
                const pendingDelta = Math.max(0, delivered - soInvoiced);
                const existingIdx = itemIndexByPart.get(estimatePartId);

                if (existingIdx !== undefined) {
                    const current = nextItems[existingIdx];
                    const currentQty = Number(current.current_invoice_qty || 0);
                    const nextQty = currentQty + pendingDelta;
                    const invoicedExcludingCurrent = Math.max(0, soInvoiced - nextQty);
                    const remainingForEdit = Math.max(0, delivered - invoicedExcludingCurrent);
                    const qtyChanged = Math.abs(nextQty - currentQty) > 1e-9;
                    const contextChanged =
                        Number(current.ordered_qty || 0) !== ordered ||
                        Number(current.delivered_qty || 0) !== delivered ||
                        Number(current.invoiced_qty || 0) !== invoicedExcludingCurrent ||
                        Number(current.remaining_invoice_qty || 0) !== remainingForEdit;
                    if (qtyChanged || contextChanged) changedLines += 1;
                    addedQtyTotal += pendingDelta;
                    nextItems[existingIdx] = {
                        ...current,
                        ordered_qty: ordered,
                        delivered_qty: delivered,
                        invoiced_qty: invoicedExcludingCurrent,
                        remaining_invoice_qty: remainingForEdit,
                        current_invoice_qty: nextQty,
                    };
                    return;
                }

                if (pendingDelta <= 1e-9) return;

                const currentQty = pendingDelta;
                const invoicedExcludingCurrent = Math.max(0, soInvoiced - currentQty);
                const remainingForEdit = Math.max(0, delivered - invoicedExcludingCurrent);
                nextItems.push({
                    estimate_part_id: estimatePartId,
                    part_name: String(soLine?.part_name || `Item ${estimatePartId}`),
                    ordered_qty: ordered,
                    delivered_qty: delivered,
                    invoiced_qty: invoicedExcludingCurrent,
                    remaining_invoice_qty: remainingForEdit,
                    current_invoice_qty: currentQty,
                    unit_price: Number(soLine?.unit_price || 0),
                });
                changedLines += 1;
                addedQtyTotal += pendingDelta;
            });

            setItems(nextItems);
            setLineErrors({});

            if (addedQtyTotal <= 1e-9) {
                toast.info('No new delivered-not-invoiced qty to sync.');
            } else {
                toast.success(`Sync complete: ${changedLines} line(s) updated, +${formatQty(addedQtyTotal)} qty added.`);
            }
        } catch (err: any) {
            console.error('Sync from deliveries failed:', err);
            setError(err?.message || 'Failed to sync delivered quantities');
        } finally {
            setSyncingDeliveries(false);
        }
    };

    const handleSave = async () => {
        const effectiveSoId = soId ? Number(soId) : Number(selectedSoId || 0);
        const isSoLinked = !!effectiveSoId;
        const effectiveEstimateId = Number(selectedEstimateId || 0);

        if (!invoiceId && !isSoLinked) {
            toast.error('Please select a sale order source');
            return;
        }

        const itemsToInvoice = items.map(item => ({
            estimate_part_id: item.estimate_part_id,
            quantity: Number(item.current_invoice_qty)
        })).filter(i => i.quantity > 0);

        if ((isSoLinked || invoiceId) && itemsToInvoice.length === 0) {
            setError("Please enter a quantity greater than 0 for at least one item.");
            return;
        }

        const nextLineErrors: Record<number, string> = {};
        if (isSoLinked) {
            items.forEach((item, idx) => {
                const current = Number(item.current_invoice_qty || 0);
                const maxQty = resolveLineMaxQty(item);
                if (Number.isNaN(current) || current < 0) {
                    nextLineErrors[idx] = 'Quantity must be 0 or greater.';
                } else if (current > maxQty + 0.001) {
                    nextLineErrors[idx] = `Max allowed is ${maxQty} (${invoiceType === 'PROFORMA' ? 'ordered-not-invoiced' : 'delivered-not-invoiced'}).`;
                }
            });
        }
        if (Object.keys(nextLineErrors).length > 0) {
            setLineErrors(nextLineErrors);
            setError('Requested quantity exceeds delivered-not-invoiced balance for one or more lines.');
            return;
        }

        setSubmitting(true);
        setError(null);
        setActiveTaxInvoiceConflict(null);
        setLineErrors({});

        try {
            let newInvoiceId: number | undefined;
            if (invoiceId) {
                await legacyApi.sales.updateInvoice(Number(invoiceId), {
                    estimate_id: Number(selectedEstimateId),
                    customer_po_no: customerPoNo,
                    invoice_date: invoiceDate,
                    invoice_notes: invoiceNotes,
                    items: itemsToInvoice
                });
                toast.success('Invoice updated successfully.');
            } else {
                const res = await legacyApi.sales.createInvoice({
                    estimate_id: effectiveEstimateId,
                    so_id: effectiveSoId || undefined,
                    wo_id: selectedWoId || undefined,
                    invoice_type: invoiceType,
                    customer_po_no: customerPoNo,
                    invoice_date: invoiceDate,
                    invoice_notes: invoiceNotes,
                    items: (effectiveSoId || itemsToInvoice.length > 0) ? itemsToInvoice : undefined
                });
                console.log('CreateInvoice response:', res);
                newInvoiceId = res.invoice_id;
                toast.success('Invoice created successfully.');

                if (res.suggest_close_so_id) {
                    const closeSoId = Number(res.suggest_close_so_id);
                    let closeAllowed = true;
                    try {
                        const precheck = await legacyApi.workflow.precheck({
                            entity: 'SO',
                            entity_id: closeSoId,
                            action: 'close',
                        });
                        closeAllowed = String(precheck.overall_status || '').toLowerCase() !== 'fail';
                    } catch {
                        // If precheck fails unexpectedly, keep legacy prompt behavior.
                        closeAllowed = true;
                    }

                    if (closeAllowed) {
                        const shouldClose = await confirm("Notice: This Order is now fully billed. Mark it as Closed?", {
                            description: "This will set the Sale Order status to Closed (Status 6).",
                            confirmText: "Yes, Close Order",
                            cancelText: "No, Keep Open"
                        });

                        if (shouldClose) {
                            try {
                                await legacyApi.sales.transitionOrder(closeSoId, 'close', 'auto close after full billing');
                                toast.success("Sale Order marked as Closed.");
                            } catch (e: any) {
                                const msg = String(e?.message || '');
                                if (msg.includes("action 'close' from status 6")) {
                                    toast.info('Sale Order is already closed.');
                                } else {
                                    console.error(e);
                                    toast.error("Failed to close order automatically.");
                                }
                            }
                        }
                    }
                }
            }
            navigate(invoiceId
                ? `/legacy/invoices/${invoiceId}`
                : (newInvoiceId ? `/legacy/invoices/${newInvoiceId}` : '/legacy/invoices')
            );
        } catch (error: any) {
            console.error('CreateInvoice error:', error);
            const apiError = error as LegacyApiError;
            const code = String(
                apiError?.error_code ||
                (apiError?.data?.error_code || apiError?.data?.detail?.error_code || '')
            );
            if (!invoiceId && code === 'SO_ACTIVE_TAX_INVOICE_EXISTS') {
                const details =
                    apiError?.details ||
                    (apiError?.data?.details && typeof apiError.data.details === 'object' ? apiError.data.details : null) ||
                    (apiError?.data?.detail?.details && typeof apiError.data.detail.details === 'object'
                        ? apiError.data.detail.details
                        : null) ||
                    {};
                const existingInvoiceId = Number(details.existing_invoice_id || 0);
                if (existingInvoiceId > 0) {
                    setActiveTaxInvoiceConflict({
                        invoice_id: existingInvoiceId,
                        status_id: Number(details.existing_status_id || 0) || null,
                        status_name: String(details.existing_status_name || '') || null,
                    });
                }
            }
            setError(error.message || 'Failed to save invoice');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-8">Loading...</div>;
    const effectiveSoIdForView = soId ? Number(soId) : Number(selectedSoId || 0);
    const isSoLinkedView = Number.isFinite(effectiveSoIdForView) && effectiveSoIdForView > 0;

    return (
        <div className="space-y-6 max-w-5xl mx-auto py-8 px-4">
            {/* ... (Header) ... */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/legacy/invoices')}>
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <h1 className="text-2xl font-bold">
                        {invoiceId ? `Edit ${formatInvoiceId(invoiceId)}` : (soId ? `New Partial Invoice (SO #${soId})` : 'New Invoice')}
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    {canSyncFromDeliveries && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleSyncFromDeliveries}
                            disabled={syncingDeliveries || submitting}
                        >
                            {syncingDeliveries ? 'Syncing...' : 'Sync from Deliveries'}
                        </Button>
                    )}
                    <Button onClick={handleSave} disabled={submitting || syncingDeliveries}>
                        <Save className="h-4 w-4 mr-2" /> {submitting ? 'Saving...' : (invoiceId ? 'Update Invoice' : 'Create Invoice')}
                    </Button>
                </div>
            </div>

            {error && (
                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <span>{error}</span>
                            {activeTaxInvoiceConflict?.invoice_id ? (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full sm:w-auto"
                                    onClick={() => {
                                        setError(null);
                                        setActiveTaxInvoiceConflict(null);
                                        const target = Number(activeTaxInvoiceConflict.status_id || 0) === 1
                                            ? `/legacy/invoices/edit/${activeTaxInvoiceConflict.invoice_id}`
                                            : `/legacy/invoices/${activeTaxInvoiceConflict.invoice_id}`;
                                        navigate(target);
                                    }}
                                >
                                    Open Existing Invoice
                                </Button>
                            ) : null}
                        </div>
                    </AlertDescription>
                </Alert>
            )}

            <div className="space-y-6">
                <div className="space-y-6">
                    <Card>
                    <CardHeader>
                        <CardTitle>Invoice Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Invoice Type</Label>
                            <select
                                value={invoiceType}
                                onChange={(e) => setInvoiceType((e.target.value === 'PROFORMA' ? 'PROFORMA' : 'TAX'))}
                                disabled={!!invoiceId}
                                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                            >
                                <option value="TAX">Tax Invoice</option>
                                <option value="PROFORMA">Proforma Invoice</option>
                            </select>
                        </div>

                        {!invoiceId && !soId && (
                            <>
                                <div className="space-y-2">
                                    <Label>Filter by Customer</Label>
                                    <EntityTypeaheadPicker
                                        value={customerFilterId ? String(customerFilterId) : ''}
                                        options={customerPickerOptions}
                                        placeholder="Filter source options by customer..."
                                        emptyText="No customers found"
                                        onChange={(value) => setCustomerFilterId(value ? Number(value) : null)}
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-2">
                                        <Label>SO Status</Label>
                                        <select
                                            value={soStatusFilter ?? ''}
                                            onChange={(e) => setSoStatusFilter(e.target.value ? Number(e.target.value) : null)}
                                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                        >
                                            <option value="">All SO Statuses</option>
                                            <option value="1">Draft</option>
                                            <option value="2">Confirmed</option>
                                            <option value="3">In Production</option>
                                            <option value="4">Ready to Ship</option>
                                            <option value="5">Dispatched</option>
                                            <option value="6">Closed</option>
                                            <option value="7">Cancelled</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Job Card Status</Label>
                                        <select
                                            value={woStatusFilter ?? ''}
                                            onChange={(e) => setWoStatusFilter(e.target.value ? Number(e.target.value) : null)}
                                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                        >
                                            <option value="">All WO Statuses</option>
                                            <option value="1">Draft</option>
                                            <option value="2">In Progress</option>
                                            <option value="3">Completed</option>
                                            <option value="4">Cancelled</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Invoice Source (SO / Job Card)</Label>
                                    <EntityTypeaheadPicker
                                        value={selectedSourceKey}
                                        options={sourcePickerOptions}
                                        loading={sourceLoading}
                                        placeholder="Search by SO, customer, or job card..."
                                        emptyText="No matching sale orders or job cards"
                                        onQueryChange={setSourceQuery}
                                        onChange={handleSelectSource}
                                    />
                                </div>
                            </>
                        )}

                        {isSoLinkedView && (
                            <div className="space-y-2">
                                <Label>Selected Source</Label>
                                <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm text-slate-700">
                                    <div className="font-medium">SO #{effectiveSoIdForView}</div>
                                    {selectedWoId ? <div className="text-xs text-slate-500">Job Card #{selectedWoId}</div> : null}
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label>Invoice Date</Label>
                            <Input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
                        </div>

                        <div className="space-y-2">
                            <Label>Customer PO Ref</Label>
                            <Input value={customerPoNo} onChange={e => setCustomerPoNo(e.target.value)} />
                        </div>

                        <div className="space-y-2">
                            <Label>Special Notes / Instructions</Label>
                            <textarea
                                className="flex min-h-[110px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                value={invoiceNotes}
                                onChange={(e) => setInvoiceNotes(e.target.value)}
                                placeholder="Enter any notes or payment instructions to appear on the invoice PDF."
                            />
                        </div>
                    </CardContent>
                </Card>

                <Card className="bg-blue-50 border-blue-100">
                    <CardHeader>
                        <CardTitle className="text-blue-900">How to use</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-blue-800 space-y-2">
                        {invoiceId ? (
                            <>
                                <p><strong>Edit Mode:</strong> Updating this invoice will reverse the previous journal entry and create a new one based on the new quantities. Use with caution.</p>
                                {canSyncFromDeliveries ? (
                                    <p><strong>Sync from Deliveries:</strong> Pull newly delivered-not-invoiced quantities into this draft invoice, then review and click Update.</p>
                                ) : null}
                            </>
                        ) : (
                            <>
                                <p>1. <strong>Partial Invoicing:</strong> Quantities can be entered per line for partial billing.</p>
                                {invoiceType === 'TAX' ? (
                                    <p>2. <strong>Delivered-only Rule:</strong> Tax invoice quantity cannot exceed delivered-not-invoiced balance.</p>
                                ) : (
                                    <p>2. <strong>Proforma Rule:</strong> Proforma quantity cannot exceed ordered-not-invoiced balance.</p>
                                )}
                            </>
                        )}
                    </CardContent>
                    </Card>
                </div>
                {(invoiceId || isSoLinkedView) && (
                    <WorkflowRightSidebar mode="overlay" title="Invoice Workflow & Status">
                        <WorkflowRightPanel
                            title="Workflow & Status"
                            description="Source context, status, and prechecks"
                        >
                            {invoiceId ? (
                                <FlowCoachPanel entity="INVOICE" entityId={Number(invoiceId)} />
                            ) : (
                                <FlowCoachPanel entity="SO" entityId={effectiveSoIdForView} />
                            )}
                        </WorkflowRightPanel>
                    </WorkflowRightSidebar>
                )}
            </div>

            {/* Items Table - Show if items exist (Edit mode or SO mode) */}
            {items.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle>Invoice Items</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="border rounded-md overflow-hidden">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-100 text-slate-600 font-semibold border-b">
                                    <tr>
                                        <th className="p-3">Part Name</th>
                                        {isSoLinkedView && <th className="p-3 text-right">Ordered</th>}
                                        {isSoLinkedView && <th className="p-3 text-right">Delivered</th>}
                                        {isSoLinkedView && <th className="p-3 text-right">{invoiceType === 'PROFORMA' ? 'Order Balance' : 'Invoiceable Balance'}</th>}
                                        <th className="p-3 text-right w-32">Invoice Qty</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {items.map((item, idx) => (
                                        <tr key={idx} className={`hover:bg-slate-50`}>
                                            <td className="p-3 font-medium">{item.part_name}</td>
                                            {isSoLinkedView && <td className="p-3 text-right font-mono">{item.ordered_qty}</td>}
                                            {isSoLinkedView && <td className="p-3 text-right font-mono text-blue-600 font-medium">{item.delivered_qty}</td>}
                                            {isSoLinkedView && <td className="p-3 text-right font-mono text-slate-500">{resolveLineMaxQty(item)}</td>}
                                            <td className="p-3 text-right">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    disabled={isSoLinkedView ? resolveLineMaxQty(item) === 0 : false}
                                                    className={`h-8 text-right font-mono ${lineErrors[idx] ? 'border-red-500 ring-2 ring-red-100' : ''}`}
                                                    value={item.current_invoice_qty}
                                                    onChange={e => handleQtyChange(idx, e.target.value)}
                                                />
                                                {lineErrors[idx] && (
                                                    <div className="mt-1 text-[11px] text-red-600 text-right">{lineErrors[idx]}</div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};
